import { useEffect, useMemo, useState } from 'react'
import { collection, doc, onSnapshot, query, where, serverTimestamp } from 'firebase/firestore'
import { addDoc, setDoc, updateDoc, deleteDoc, writeBatch } from '../../core/db'
import { db } from '../../core/firebase'
import type { FpdConfig, FpdDay, FpdEvent, FpdPost, FpdRequest, FpdStatus, FpdYear, Scope } from '../../core/types'
import { DEFAULT_CONFIG, BUILDING_SCOPES, type RequestForm } from './logic'

/**
 * Flex PD's data: school years with their required hours, the flex days, and
 * the district's option lists (config/flexpd). Every staff member reads all
 * three (a teacher needs the calendar and the requirement); only the district
 * office writes them, and the rules hold that to `flexpd.configure@district`.
 */

export function useFpdYear(id: string) {
  const [year, setYear] = useState<FpdYear | null | undefined>(undefined)
  useEffect(() => {
    if (!id) return
    return onSnapshot(doc(db, 'fpdYears', id),
      (s) => setYear(s.exists() ? ({ id: s.id, ...s.data() } as FpdYear) : null),
      () => setYear(null))
  }, [id])
  return year
}

export function useFpdDays(yearId: string) {
  const [days, setDays] = useState<FpdDay[] | null>(null)
  useEffect(() => {
    if (!yearId) return
    return onSnapshot(query(collection(db, 'fpdDays'), where('yearId', '==', yearId)),
      (s) => setDays(s.docs.map((d) => ({ id: d.id, ...d.data() }) as FpdDay)),
      () => setDays([]))
  }, [yearId])
  return days
}

/** The district's lists, or the defaults until someone saves them. */
export function useFpdConfig() {
  const [cfg, setCfg] = useState<FpdConfig | null>(null)
  useEffect(() => onSnapshot(doc(db, 'config', 'flexpd'),
    (s) => setCfg(s.exists() ? { ...DEFAULT_CONFIG, ...(s.data() as FpdConfig) } : DEFAULT_CONFIG),
    () => setCfg(DEFAULT_CONFIG)), [])
  return cfg
}

export async function saveYear(y: Pick<FpdYear, 'id' | 'label' | 'start' | 'end' | 'totalHours' | 'collabHours'>, by: string) {
  const { id, ...rest } = y
  await setDoc(doc(db, 'fpdYears', id), { ...rest, updatedBy: by, updatedAt: serverTimestamp() })
}

export type DayInput = Pick<FpdDay, 'date' | 'buildings' | 'cap' | 'note' | 'closed'>

export async function addDay(yearId: string, d: DayInput, by: string) {
  await addDoc(collection(db, 'fpdDays'), { yearId, ...d, note: d.note.trim(), updatedBy: by, updatedAt: serverTimestamp() })
}

export async function updateDay(id: string, d: DayInput, by: string) {
  await updateDoc(doc(db, 'fpdDays', id), { ...d, note: d.note.trim(), updatedBy: by, updatedAt: serverTimestamp() })
}

/** Only for a date entered by mistake. Once requests exist, a day is closed, not removed. */
export async function removeDay(id: string) {
  await deleteDoc(doc(db, 'fpdDays', id))
}

export async function saveConfig(c: FpdConfig, by: string) {
  const clean = (l: FpdConfig['focus']) => l.map((o) => ({ name: o.name.trim(), ...(o.retired ? { retired: true } : {}) })).filter((o) => o.name)
  await setDoc(doc(db, 'config', 'flexpd'), {
    focus: clean(c.focus), places: clean(c.places), sendBack: clean(c.sendBack), updatedBy: by, updatedAt: serverTimestamp(),
  })
}

/** Every building this person works in for Flex PD, from what they hold. */
export function fpdScopes(caps: string[] | undefined, districtAdmin?: boolean): Scope[] | 'all' {
  if (districtAdmin) return 'all'
  const s = new Set<Scope>()
  for (const g of caps ?? []) if (g.startsWith('flexpd.')) s.add(g.split('@')[1] as Scope)
  return s.has('district') ? 'all' : [...s]
}

/* ---------------- requests ---------------- */

const asRequest = (d: { id: string; data: () => unknown }) => ({ id: d.id, ...(d.data() as object) }) as FpdRequest

/** A teacher's own requests this year. */
export function useMyRequests(yearId: string, email: string, enabled: boolean) {
  const [rows, setRows] = useState<FpdRequest[] | null>(null)
  useEffect(() => {
    if (!enabled || !yearId || !email) { setRows([]); return }
    return onSnapshot(query(collection(db, 'fpdRequests'), where('teacherEmail', '==', email), where('yearId', '==', yearId)),
      (s) => setRows(s.docs.map(asRequest)), () => setRows([]))
  }, [yearId, email, enabled])
  return rows
}

/**
 * Every request in the buildings this person may see, one listener per
 * building: the rules allow a list only when it is filtered to a building
 * they hold approve or view in.
 */
export function useBuildingRequests(yearId: string, buildings: Scope[]) {
  const key = buildings.join(',')
  const [byB, setByB] = useState<Record<string, FpdRequest[]>>({})
  useEffect(() => {
    setByB({})
    if (!yearId || !buildings.length) return
    const offs = buildings.map((b) => onSnapshot(
      query(collection(db, 'fpdRequests'), where('building', '==', b), where('yearId', '==', yearId)),
      (s) => setByB((x) => ({ ...x, [b]: s.docs.map(asRequest) })),
      () => setByB((x) => ({ ...x, [b]: [] }))))
    return () => offs.forEach((o) => o())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, key])
  const loaded = buildings.every((b) => b in byB)
  return useMemo(() => (loaded ? buildings.flatMap((b) => byB[b] ?? []) : null), [byB, loaded, key]) // eslint-disable-line react-hooks/exhaustive-deps
}

export function useRequest(id: string) {
  const [r, setR] = useState<FpdRequest | null | undefined>(undefined)
  useEffect(() => {
    if (!id) return
    return onSnapshot(doc(db, 'fpdRequests', id), (s) => setR(s.exists() ? asRequest(s) : null), () => setR(null))
  }, [id])
  return r
}

export function useRequestEvents(id: string, enabled: boolean) {
  const [rows, setRows] = useState<FpdEvent[]>([])
  useEffect(() => {
    if (!id || !enabled) return
    return onSnapshot(collection(db, 'fpdRequests', id, 'events'),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as FpdEvent)
        .sort((a, b) => ((a.at as { toMillis?: () => number })?.toMillis?.() ?? 0) - ((b.at as { toMillis?: () => number })?.toMillis?.() ?? 0))),
      () => setRows([]))
  }, [id, enabled])
  return rows
}

const clean = (f: RequestForm & { postId?: string }): RequestForm & { postId?: string } => ({
  ...f, place: f.place.trim(), focus: f.focus.trim(), title: f.title.trim(), description: f.description.trim(),
  evidence: f.evidence.trim(), collaborators: f.collaborative ? f.collaborators : [],
})

interface Me { email: string; name: string }

/**
 * The teacher saves (a draft, or edits kept at the same status) or sends
 * (to `send`, with a history entry in the same batch). Returns the request id.
 */
export async function saveRequest(
  yearId: string, me: Me, f: RequestForm & { postId?: string }, existing: FpdRequest | undefined, send?: 'submitted' | 'completed',
): Promise<string> {
  const ref = existing ? doc(db, 'fpdRequests', existing.id) : doc(collection(db, 'fpdRequests'))
  const { building, postId, ...rest } = clean(f)
  const body = { yearId, ...rest, ...(postId ? { postId } : {}) }
  const batch = writeBatch(db)
  const status: FpdStatus = send ?? existing?.status ?? 'draft'
  let lastEventId: string | undefined
  if (send) {
    const ev = doc(collection(ref, 'events'))
    lastEventId = ev.id
    batch.set(ev, {
      at: serverTimestamp(), byEmail: me.email, byName: me.name, statusAfter: send, note: '', reasons: [],
      action: send === 'completed' ? 'completed' : existing && existing.status !== 'draft' ? 'resubmitted' : 'submitted',
    })
  }
  if (existing) {
    batch.update(ref, { ...body, status, ...(lastEventId ? { lastEventId } : {}), updatedAt: serverTimestamp() })
  } else {
    batch.set(ref, {
      ...body, building, teacherEmail: me.email, teacherName: me.name, status, note: '', reasons: [],
      ...(lastEventId ? { lastEventId } : {}), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
    })
  }
  await batch.commit()
  return ref.id
}

/** After the day: what happened. Moves an approved request to be verified. */
export async function markDone(r: FpdRequest, me: Me, evidence: string) {
  const ref = doc(db, 'fpdRequests', r.id)
  const ev = doc(collection(ref, 'events'))
  const batch = writeBatch(db)
  batch.update(ref, { evidence: evidence.trim(), status: 'completed', lastEventId: ev.id, updatedAt: serverTimestamp() })
  batch.set(ev, { at: serverTimestamp(), byEmail: me.email, byName: me.name, action: 'completed', statusAfter: 'completed', note: '', reasons: [] })
  await batch.commit()
}

/** The principal's decision, with its note and any canned reasons. */
export async function decide(r: FpdRequest, me: Me, to: 'approved' | 'returned' | 'denied' | 'verified', note: string, reasons: string[]) {
  const ref = doc(db, 'fpdRequests', r.id)
  const ev = doc(collection(ref, 'events'))
  const batch = writeBatch(db)
  batch.update(ref, { status: to, note: note.trim(), reasons, decidedBy: me.name, lastEventId: ev.id, updatedAt: serverTimestamp() })
  batch.set(ev, { at: serverTimestamp(), byEmail: me.email, byName: me.name, action: to, statusAfter: to, note: note.trim(), reasons })
  await batch.commit()
}

export async function deleteDraft(id: string) {
  await deleteDoc(doc(db, 'fpdRequests', id))
}

/** Buildings for a capability, with district-wide access spelled out as every building. */
export function buildingsFor(caps: string[] | undefined, districtAdmin: boolean | undefined, ...capNames: string[]): Scope[] {
  if (districtAdmin) return BUILDING_SCOPES
  const s = new Set<Scope>()
  for (const g of caps ?? []) {
    const [c, scope] = g.split('@')
    if (!capNames.includes(c!)) continue
    if (scope === 'district') BUILDING_SCOPES.forEach((b) => s.add(b))
    else if (BUILDING_SCOPES.includes(scope as Scope)) s.add(scope as Scope)
  }
  return BUILDING_SCOPES.filter((b) => s.has(b))
}

/**
 * The Flex PD teachers in each building this person may see, one query per
 * building: the rules let a principal or coach list only their own
 * building's teachers, and only when the query asks for exactly them.
 */
export function useBuildingTeachers(buildings: Scope[]) {
  const key = buildings.join(',')
  const [byB, setByB] = useState<Record<string, Array<{ email: string; name: string; building: Scope }>>>({})
  useEffect(() => {
    setByB({})
    if (!buildings.length) return
    const offs = buildings.map((b) => onSnapshot(
      query(collection(db, 'users'), where('caps', 'array-contains', `flexpd.request@${b}`)),
      (s) => setByB((x) => ({ ...x, [b]: s.docs.filter((d) => d.get('active') !== false)
        .map((d) => ({ email: d.id, name: String(d.get('displayName') ?? d.id), building: b })) })),
      () => setByB((x) => ({ ...x, [b]: [] }))))
    return () => offs.forEach((o) => o())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const loaded = buildings.every((b) => b in byB)
  return useMemo(() => (loaded ? buildings.flatMap((b) => byB[b] ?? []) : null), [byB, loaded, key]) // eslint-disable-line react-hooks/exhaustive-deps
}

/* ---------------- collaboration posts ---------------- */

export function usePosts(yearId: string, enabled: boolean) {
  const [rows, setRows] = useState<FpdPost[] | null>(null)
  useEffect(() => {
    if (!enabled || !yearId) { setRows([]); return }
    return onSnapshot(query(collection(db, 'fpdPosts'), where('yearId', '==', yearId)),
      (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as FpdPost)), () => setRows([]))
  }, [yearId, enabled])
  return rows
}

export function usePost(id: string) {
  const [p, setP] = useState<FpdPost | null | undefined>(undefined)
  useEffect(() => {
    if (!id) { setP(null); return }
    return onSnapshot(doc(db, 'fpdPosts', id), (s) => setP(s.exists() ? ({ id: s.id, ...s.data() }) as FpdPost : null), () => setP(null))
  }, [id])
  return p
}

export type PostInput = Pick<FpdPost, 'date' | 'startMin' | 'endMin' | 'focus' | 'place' | 'idea' | 'spots'>

export async function createPost(yearId: string, me: { email: string; name: string }, building: Scope, x: PostInput) {
  await addDoc(collection(db, 'fpdPosts'), {
    yearId, ...x, focus: x.focus.trim(), place: x.place.trim(), idea: x.idea.trim(),
    organizerEmail: me.email, organizerName: me.name, organizerBuilding: building,
    memberEmails: [], members: [], open: true, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  })
}

export async function setPostOpen(p: FpdPost, open: boolean) {
  await updateDoc(doc(db, 'fpdPosts', p.id), { open, updatedAt: serverTimestamp() })
}

export async function joinPost(p: FpdPost, me: { email: string; name: string; building: Scope }) {
  await updateDoc(doc(db, 'fpdPosts', p.id), {
    memberEmails: [...p.memberEmails, me.email],
    members: [...p.members, { email: me.email, name: me.name, building: me.building }],
    updatedAt: serverTimestamp(),
  })
}

export async function leavePost(p: FpdPost, email: string) {
  await updateDoc(doc(db, 'fpdPosts', p.id), {
    memberEmails: p.memberEmails.filter((e) => e !== email),
    members: p.members.filter((m) => m.email !== email),
    updatedAt: serverTimestamp(),
  })
}

export async function deletePost(id: string) {
  await deleteDoc(doc(db, 'fpdPosts', id))
}
